"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.WizardStepRegistry = void 0;
exports.WizardStepRegistry = {
    init: init,
    register: register,
    registerNext: registerNext,
    getStepById: getStepById,
    getFirstIncompleteStep: getFirstIncompleteStep,
};
//////////
var stepsById = new Map();
var stateAccessor;
/**
 * Connects the step registry to the global store.
 *
 * @param getState State accessor function.
 */
function init(getState) {
    stateAccessor = getState;
}
var getRootState = function () {
    if (!stateAccessor)
        throw new Error('The wizard cannot access your global store. Please call `WizardStepRegistry.init`');
    return stateAccessor();
};
/**
 * Registers the provided configuration as a logical step.
 *
 * @param id Unique identifier for the step
 * @param config Configuration for the step
 */
function register(id, config) {
    if (stepsById.has(id)) {
        throw new Error("Step '".concat(id, "' has already been registered"));
    }
    var def = new WizardStepDef(id, config);
    stepsById.set(id, def);
    return def;
}
/**
 * Registers the provided configuration as the "next" logical step.
 *
 * @description
 * The provided step will automatically be configured to point to the previously registered "last step" as long as no
 * existing `getPreviousStepId` method has been implemented.
 *
 * Similarly, the previously registered "last step" will be re-configured to point to the provided step as the "next"
 * step, as long as no existing `getNextStepId` method has been implemented.
 *
 * @param id Unique identifier for the step
 * @param config Configuration for the step
 */
function registerNext(id, config) {
    var lastStep = Array.from(stepsById.values()).pop();
    if (lastStep) {
        if (!lastStep.config.getNextStepId)
            lastStep.config.getNextStepId = function () { return id; };
        if (!config.getPreviousStepId)
            config.getPreviousStepId = function () { return lastStep.id; };
    }
    return register(id, config);
}
var lookupStepByKey = function (key, steps) {
    if (!key)
        return undefined;
    var step = steps.get(key);
    if (step)
        return step;
    console.warn("Could not find step using key: '".concat(key, "'. Valid keys are: ").concat(Array.from(steps.keys())
        .map(function (k) { return "'".concat(k, "'"); })
        .join(', ')));
    return undefined;
};
var getStepUrlPrefix = function (url) {
    var prefix = (url.startsWith('/') ? url.substring(1).split('/') : url.split('/'))[0];
    return prefix;
};
/**
 * Finds the step definition registered with the given id, or `undefined` if no step is found.
 *
 * @param id Identifies the step to retrieve.
 */
function getStepById(id) {
    return lookupStepByKey(id, stepsById);
}
var runTransition = function (def, getStepCalculator) {
    var state = getRootState();
    // Does the config declare a getNextStepId / getPreviousStepId ?
    var stepCalculator = getStepCalculator && getStepCalculator(def.config);
    if (!stepCalculator)
        return undefined;
    var stepId = stepCalculator(state);
    if (!stepId) {
        // This is an error - if the step declares that it has a previous/next step, then it always has to return
        // one when asked.
        throw new Error("Cannot run transition - the step calculator for '".concat(def.id, "' did not return a step id. ").concat(stepCalculator));
    }
    var step = getStepById(stepId);
    return step && step.shouldSkip() ? runTransition(step, getStepCalculator) : step;
};
/**
 * Works backwards starting from the given step, until the previous step reports as completed.
 *
 * @param startAt The first step to be checked.
 * @returns The first step found where the previous step reports as being complete, or `undefined` if the starting step
 * was undefined, or if no incomplete steps were found.
 */
function getFirstIncompleteStep(startAt) {
    if (!startAt)
        return undefined;
    var stack = [];
    for (var step = startAt; step != null; step = step.getPreviousStep && step.getPreviousStep()) {
        stack.push([step, step.isComplete()]);
    }
    while (stack.length > 0) {
        var _a = stack.pop(), step = _a[0], isComplete = _a[1];
        if (!isComplete)
            return step;
    }
    return undefined;
}
//////////
var WizardStepDef = /** @class */ (function () {
    function WizardStepDef(id, config) {
        var _this = this;
        this.id = id;
        this.config = config;
        /**
         * Gets the URL to use for navigating to this step
         */
        this.getUrl = function () { return (_this.config.getUrl ? _this.config.getUrl() : _this.id); };
        /**
         * Determines if this step can be skipped based on the current state
         */
        this.shouldSkip = function () { return !!(_this.config.canSkip && _this.config.canSkip(getRootState())); };
        /**
         * Checks if this step can be considered as "completed" based on the current state. This will always return `true` if
         * the step can be skipped.
         */
        this.isComplete = function () {
            if (!_this.config.isComplete)
                return true;
            var state = getRootState();
            return _this.config.isComplete(state) || !!(_this.config.canSkip && _this.config.canSkip(state));
        };
        this.getPreviousStep = function () {
            return runTransition(_this, function (config) { return config.getPreviousStepId; });
        };
        this.getNextStep = function () { return runTransition(_this, function (config) { return config.getNextStepId; }); };
        this.isValid = function () {
            console.debug("Validating step: ".concat(_this.id, " ").concat(JSON.stringify(_this)));
            var visitedSteps = new Set([_this.id]);
            return isPreviousStepCompleteRecursive(_this);
            //////////
            function isPreviousStepCompleteRecursive(step) {
                // If there's no steps before this one, then it's valid to be here.
                if (!step.hasPreviousStep) {
                    console.debug("  [There are no previous steps]");
                    return true;
                }
                // This is an error - if the step declares that it has a previous step, it must always return one.
                var previousStep = step.getPreviousStep();
                if (!previousStep) {
                    throw new Error('Could not calculate the previous step');
                }
                // If we have already visited this step, then the steps have been put together incorrectly - it's a cyclic reference
                if (visitedSteps.has(previousStep.id)) {
                    var stepsOrder = Array.from(visitedSteps.values());
                    stepsOrder.unshift(step.id);
                    throw new Error("  Cyclic reference detected in steps: ".concat(stepsOrder.join(' <- ')));
                }
                // If the user hasn't completed the step before this one then they're too far forward
                if (!previousStep.isComplete()) {
                    console.warn("  Previous step '".concat(previousStep.id, "' is incomplete"));
                    return false;
                }
                console.debug("  Previous step '".concat(previousStep.id, "' has been completed"));
                return isPreviousStepCompleteRecursive(previousStep);
            }
        };
    }
    Object.defineProperty(WizardStepDef.prototype, "hasPreviousStep", {
        get: function () {
            return !!this.config.getPreviousStepId;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(WizardStepDef.prototype, "hasNextStep", {
        get: function () {
            return !!this.config.getNextStepId;
        },
        enumerable: false,
        configurable: true
    });
    return WizardStepDef;
}());
//# sourceMappingURL=WizardStepRegistry.js.map